import {
  ExtendTimeButton,
  type ExtendTimeButtonProps,
  TimeExtensionHistoryButton,
  type TimeExtensionHistoryButtonProps,
} from '@exam-center/exam-event-time-extension';
import { formatStartToEndTime } from '@exam-center/utils';
import { Button, Flex, Text } from '@shared/components';
import { adjustTime } from '@shared/utils';
import { useMemo } from 'react';

import ExamRoundNumberTag from './exam-round-number-tag';
import { calculateDuration } from './utils/utils';

export interface ExamSessionCardProps {
  examRoundNumber: number;
  startTime: Date;
  endTime: Date;
  onClickProctoring?: () => void;
  disabledProctoring?: boolean;
  timeAdjustment: number;
  timeExtension?: Omit<ExtendTimeButtonProps, 'postponeDuration'>;
  timeExtensionHistory?: Omit<TimeExtensionHistoryButtonProps, 'postponeDuration'>;
}

export default function ExamSessionCard(props: ExamSessionCardProps) {
  const {
    examRoundNumber,
    startTime,
    endTime,
    onClickProctoring,
    disabledProctoring,
    timeAdjustment,
    timeExtension,
    timeExtensionHistory,
  } = props;

  const { displayTime, originalTimeFormatted, duration } = useMemo(() => {
    if (!timeAdjustment) {
      const rawFormatted = formatStartToEndTime(startTime, endTime);
      return {
        displayTime: rawFormatted,
        originalTimeFormatted: null,
        duration: calculateDuration(startTime, endTime),
      };
    }

    const adjustedStartTime = adjustTime(startTime, timeAdjustment);
    const adjustedEndTime = adjustTime(endTime, timeAdjustment);

    return {
      displayTime: formatStartToEndTime(adjustedStartTime, adjustedEndTime),
      originalTimeFormatted: formatStartToEndTime(startTime, endTime),
      duration: calculateDuration(adjustedStartTime, adjustedEndTime),
    };
  }, [startTime, endTime, timeAdjustment]);

  return (
    <Flex
      sx={(theme) => ({
        justifyContent: 'space-between',
        alignItems: 'center',
        border: '1px solid',
        borderColor: theme.palette.grey[300],
        paddingX: theme.spacing(6),
        paddingY: theme.spacing(4),
        borderRadius: theme.shape.rounded(2),
      })}
    >
      <Flex
        sx={(theme) => ({
          flexDirection: 'column',
          gap: theme.spacing(3),
        })}
      >
        <Flex sx={(theme) => ({ alignItems: 'center', gap: theme.spacing(3) })}>
          <ExamRoundNumberTag roundNumber={examRoundNumber} />
          <Text sx={{ fontWeight: 600 }}>{displayTime}</Text>
          {timeAdjustment ? <Text variant='body2'>(เวลาเดิม: {originalTimeFormatted})</Text> : null}
        </Flex>
        <Text sx={{ fontWeight: 400 }}>ระยะเวลาสอบ: {duration}</Text>
      </Flex>
      <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
        <Button onClick={onClickProctoring} disabled={disabledProctoring}>
          คุมสอบ
        </Button>
        <ExtendTimeButton
          {...(timeExtension as ExtendTimeButtonProps)}
          postponeDuration={timeAdjustment}
        />
        <TimeExtensionHistoryButton
          {...(timeExtensionHistory as TimeExtensionHistoryButtonProps)}
          postponeDuration={timeAdjustment}
        />
      </Flex>
    </Flex>
  );
}
